import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  LinearProgress,
  Link as MuiLink,
  Menu,
  Checkbox,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link, useParams } from 'react-router-dom';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';
import { useBrand } from '../../brands/BrandProvider';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { eventService } from '../../services/eventService';
import { registrationService, Registration } from '../../services/dashboardService';
import api from '../../services/api';
import EventWorkspaceCheckInTab from '../../components/checkin/EventWorkspaceCheckInTab';
import EventWorkspaceCustomDomainTab from '../../components/event-workspace/EventWorkspaceCustomDomainTab';
import EventWorkspaceMarketingTab from '../../components/event-workspace/EventWorkspaceMarketingTab';
import EventWorkspaceAtendimentoTab from '../../components/event-workspace/EventWorkspaceAtendimentoTab';
import MetaIntegrationNotice, { readMetaIntegration } from '../../components/event-workspace/MetaIntegrationNotice';
import EventRegistrationQuickEditPanel from '../../components/event-workspace/EventRegistrationQuickEditPanel';
import { buildWhatsAppUrl, formatPhoneNumber } from '../../utils/phoneValidation';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useWithdrawalGlobalState } from '../../hooks/useWithdrawalGlobalState';
import { normalizeWithdrawalPanelMode } from '../../utils/parishWithdrawalPanel';
import SaquePage from './SaquePage';
import { getEventCustomDomain } from '../../services/customDomainService';
import GroupOrderChip from '../../components/GroupOrderChip';
import {
  PAYMENT_METHOD_FILTER_OPTIONS,
  canonicalPaymentMethod,
  paymentMethodDisplay,
} from '../../utils/paymentMethodLabel';
import {
  registrationPaymentChipColor,
  registrationPaymentLabel,
} from '../../utils/registrationStatusLabel';
import RegistrationDetailsModal from '../../components/RegistrationDetailsModal';

type WorkspaceEvent = {
  _id: string;
  title: string;
  slug?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  city?: string;
  state?: string;
  capacity?: number;
  /** Contagem paga (soma de quantity) vinda do backend — mesma regra da página pública. */
  registeredCount?: number;
  remainingSpots?: number | null;
  showRemainingSpots?: boolean;
  metaPixel?: {
    pixelId?: string;
    enabled?: boolean;
    capiTokenConfigured?: boolean;
  };
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const getRegistrationPlatformFee = (reg: Registration) => {
  if (typeof reg.parishFee === 'number') return reg.parishFee;
  const feePercentage = typeof reg.feePercentage === 'number' ? reg.feePercentage : 6.9;
  const amount = reg.amountPaid || 0;
  return parseFloat((amount * (feePercentage / 100)).toFixed(2));
};

const getEventStatusLabel = (status?: string) => {
  switch ((status || '').toLowerCase()) {
    case 'published':
      return 'Publicado';
    case 'paused':
      return 'Pausado';
    case 'test':
      return 'Modo teste';
    case 'pre_published':
      return 'Pré-publicado';
    case 'cancelled':
      return 'Cancelado';
    case 'completed':
      return 'Finalizado';
    default:
      return 'Não definido';
  }
};

const normalizeFieldKey = (key: string) =>
  key.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const isBasicFormFieldKey = (key: string) => {
  const normalized = normalizeFieldKey(key);
  return ['name', 'email', 'phone', 'telefone', 'numero', 'celular', 'whatsapp'].includes(normalized);
};

const resolveRegistrationPhone = (formData?: Record<string, unknown>): string => {
  if (!formData) return '-';
  if (formData.phone) return String(formData.phone);
  if (formData.telefone) return String(formData.telefone);
  for (const [key, value] of Object.entries(formData)) {
    if (value != null && value !== '' && ['numero', 'celular', 'whatsapp'].includes(normalizeFieldKey(key))) {
      return String(value);
    }
  }
  return '-';
};

const RegistrationPhoneCell: React.FC<{ formData?: Record<string, unknown> }> = ({ formData }) => {
  const raw = resolveRegistrationPhone(formData);
  if (raw === '-') return <>-</>;

  const display = formatPhoneNumber(raw) || raw;
  const whatsappUrl = buildWhatsAppUrl(raw);
  if (!whatsappUrl) return <>{display}</>;

  return (
    <Button
      size="small"
      variant="outlined"
      color="success"
      startIcon={<WhatsAppIcon fontSize="small" />}
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {display}
    </Button>
  );
};

const EventWorkspacePage: React.FC = () => {
  const { eventId } = useParams();
  const t = useBrandRegistrationCopy();
  const brand = useBrand();
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: withdrawalGlobalState, isError: withdrawalGlobalStateError } = useWithdrawalGlobalState();
  const withdrawalPanelMode = normalizeWithdrawalPanelMode(user?.parish?.features?.withdrawalPanelMode);
  const isMainParish = user?.parish?.isMainParish !== false;
  const globalWithdrawalsOk =
    withdrawalGlobalStateError ||
    withdrawalGlobalState == null ||
    withdrawalGlobalState.enabled !== false;
  const showSaqueTab =
    user?.userType === 'parish_admin' &&
    user?.allowWithdrawalAccess !== false &&
    isMainParish &&
    globalWithdrawalsOk &&
    withdrawalPanelMode !== 'hidden';
  /** Páginas em domínio próprio: só Meu Sacramento com a feature ligada. Zuadaria usa o próprio host. */
  const showDomainTab =
    brand.key !== 'zuadaria' && user?.parish?.features?.customDomainPagesEnabled === true;

  const tabOffsetAfterFinance = (showSaqueTab ? 1 : 0);
  const workspaceTabs = {
    config: 2 + tabOffsetAfterFinance,
    form: 3 + tabOffsetAfterFinance,
    checkin: 4 + tabOffsetAfterFinance,
    domain: showDomainTab ? 5 + tabOffsetAfterFinance : -1,
    atendimento: 5 + tabOffsetAfterFinance + (showDomainTab ? 1 : 0),
    marketing: 6 + tabOffsetAfterFinance + (showDomainTab ? 1 : 0),
  };

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventData, setEventData] = useState<WorkspaceEvent | null>(null);
  const [eventRegistrations, setEventRegistrations] = useState<Registration[]>([]);
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'pending' | 'refunded' | 'failed' | 'expired'>('all');
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState<'all' | 'confirmed' | 'pending' | 'attended' | 'cancelled' | 'expired'>('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [ticketFilter, setTicketFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [registrationsPage, setRegistrationsPage] = useState(1);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [spotsToggleUpdating, setSpotsToggleUpdating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    codigo: true,
    participante: true,
    email: true,
    telefone: true,
    ingresso: true,
    pagamento: true,
    metodo: true,
    valor: true,
    data: true,
    checkin: true,
  });
  const [customDomainPublicUrl, setCustomDomainPublicUrl] = useState<string | null>(null);
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const rowsPerPage = 12;

  useEffect(() => {
    if (!eventId) return;

    setLoading(true);
    setError(null);

    Promise.all([
      eventService.getEventWorkspace(eventId),
      registrationService.getByEvent(eventId),
      getEventCustomDomain(eventId).catch(() => null),
    ])
      .then(([eventResponse, registrations, customDomain]) => {
        const eventPayload = eventResponse?.data ?? eventResponse;
        const parsedEvent: WorkspaceEvent = {
          _id: eventPayload?._id ?? eventId,
          title: eventPayload?.title ?? 'Evento',
          slug: eventPayload?.slug,
          status: eventPayload?.status,
          startDate: eventPayload?.startDate,
          endDate: eventPayload?.endDate,
          location: eventPayload?.location,
          city: eventPayload?.city,
          state: eventPayload?.state,
          capacity: eventPayload?.capacity,
          registeredCount:
            typeof eventPayload?.registeredCount === 'number' ? eventPayload.registeredCount : undefined,
          remainingSpots:
            typeof eventPayload?.remainingSpots === 'number' ? eventPayload.remainingSpots : null,
          showRemainingSpots: eventPayload?.showRemainingSpots === true,
          metaPixel: {
            pixelId: eventPayload?.metaPixel?.pixelId,
            enabled: eventPayload?.metaPixel?.enabled === true,
            capiTokenConfigured: eventPayload?.metaPixel?.capiTokenConfigured === true,
          },
        };
        setEventData(parsedEvent);
        setEventRegistrations(registrations || []);
        setCustomDomainPublicUrl(
          customDomain?.status === 'active' && customDomain.publicUrl ? customDomain.publicUrl : null
        );
      })
      .catch((err) => {
        console.error('Erro ao carregar workspace do evento:', err);
        setError('Não foi possível carregar os dados do evento.');
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const metrics = useMemo(() => {
    const total = eventRegistrations.length;
    const paid = eventRegistrations.filter((reg) => reg.paymentStatus === 'paid').length;
    const pending = eventRegistrations.filter(
      (reg) => reg.status === 'pending' || reg.paymentStatus === 'pending'
    ).length;
    const confirmed = eventRegistrations.filter(
      (reg) => reg.status === 'confirmed' || reg.status === 'attended'
    ).length;
    const revenue = eventRegistrations
      .filter((reg) => reg.paymentStatus === 'paid')
      .reduce((sum, reg) => sum + (reg.amountPaid || 0), 0);
    const fees = eventRegistrations
      .filter((reg) => reg.paymentStatus === 'paid')
      .reduce((sum, reg) => sum + getRegistrationPlatformFee(reg), 0);
    return { total, paid, pending, confirmed, revenue, fees };
  }, [eventRegistrations]);

  const financeMetrics = useMemo(() => {
    const paidConfirmedRegs = eventRegistrations.filter(
      (reg) => reg.paymentStatus === 'paid' && (reg.status === 'confirmed' || reg.status === 'attended')
    );
    const paidConfirmed = paidConfirmedRegs.length;
    const paidConfirmedRevenue = paidConfirmedRegs.reduce((sum, reg) => sum + (reg.amountPaid || 0), 0);

    const freeConfirmed = paidConfirmedRegs.filter((reg) => (reg.amountPaid || 0) <= 0).length;

    const pending = eventRegistrations.filter(
      (reg) => reg.status === 'pending' || reg.paymentStatus === 'pending'
    ).length;
    const expired = eventRegistrations.filter((reg) => reg.status === 'expired').length;

    const pendingValue = eventRegistrations
      .filter((reg) => reg.status === 'pending' || reg.paymentStatus === 'pending')
      .reduce((sum, reg) => sum + (reg.amountPaid || 0), 0);

    const platformFees = paidConfirmedRegs.reduce((sum, reg) => sum + getRegistrationPlatformFee(reg), 0);
    const feePercentage =
      paidConfirmedRegs.find((reg) => typeof reg.feePercentage === 'number')?.feePercentage ?? 6.9;
    const netRevenue = Math.max(0, parseFloat((paidConfirmedRevenue - platformFees).toFixed(2)));

    const ticketBreakdownMap = new Map<
      string,
      {
        ticket: string;
        total: number;
        paidConfirmed: number;
        freeConfirmed: number;
        pending: number;
        expired: number;
        revenue: number;
        fees: number;
      }
    >();

    eventRegistrations.forEach((reg) => {
      const ticket = reg.ticketName?.trim() || 'Não identificado';
      const current = ticketBreakdownMap.get(ticket) || {
        ticket,
        total: 0,
        paidConfirmed: 0,
        freeConfirmed: 0,
        pending: 0,
        expired: 0,
        revenue: 0,
        fees: 0,
      };

      current.total += 1;

      const isPaidConfirmed = reg.paymentStatus === 'paid' && (reg.status === 'confirmed' || reg.status === 'attended');
      if (isPaidConfirmed) {
        current.paidConfirmed += 1;
        current.revenue += reg.amountPaid || 0;
        current.fees += getRegistrationPlatformFee(reg);
        if ((reg.amountPaid || 0) <= 0) {
          current.freeConfirmed += 1;
        }
      }

      if (reg.status === 'pending' || reg.paymentStatus === 'pending') {
        current.pending += 1;
      }

      if (reg.status === 'expired') {
        current.expired += 1;
      }

      ticketBreakdownMap.set(ticket, current);
    });

    const ticketBreakdown = Array.from(ticketBreakdownMap.values()).sort((a, b) => b.revenue - a.revenue);

    return {
      paidConfirmed,
      paidConfirmedRevenue,
      freeConfirmed,
      pending,
      expired,
      pendingValue,
      platformFees,
      feePercentage,
      netRevenue,
      ticketBreakdown,
    };
  }, [eventRegistrations]);

  const filteredRegistrations = useMemo(() => {
    let result = [...eventRegistrations];
    if (paymentFilter !== 'all') {
      result = result.filter((reg) =>
        paymentFilter === 'expired'
          ? reg.paymentStatus === 'expired' || reg.status === 'expired'
          : reg.paymentStatus === paymentFilter
      );
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (reg) =>
          reg.participantName?.toLowerCase().includes(term) ||
          reg.participantEmail?.toLowerCase().includes(term) ||
          reg.confirmationCode?.toLowerCase().includes(term) ||
          resolveRegistrationPhone(reg.formData).toLowerCase().includes(term)
      );
    }
    if (registrationStatusFilter !== 'all') {
      result = result.filter((reg) => reg.status === registrationStatusFilter);
    }
    if (methodFilter !== 'all') {
      result = result.filter((reg) => canonicalPaymentMethod(reg.paymentMethod) === methodFilter);
    }
    if (ticketFilter !== 'all') {
      result = result.filter((reg) => reg.ticketName === ticketFilter);
    }
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter((reg) => new Date(reg.createdAt) >= start);
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((reg) => new Date(reg.createdAt) <= end);
    }
    return result;
  }, [eventRegistrations, paymentFilter, searchTerm, registrationStatusFilter, methodFilter, ticketFilter, startDate, endDate]);

  const activeFilterCount = [
    paymentFilter !== 'all',
    registrationStatusFilter !== 'all',
    methodFilter !== 'all',
    ticketFilter !== 'all',
    Boolean(startDate),
    Boolean(endDate),
    Boolean(searchTerm.trim()),
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  const filteredMetrics = useMemo(() => {
    const total = filteredRegistrations.length;
    const confirmed = filteredRegistrations.filter(
      (reg) => reg.status === 'confirmed' || reg.status === 'attended'
    ).length;
    const pending = filteredRegistrations.filter(
      (reg) => reg.status === 'pending' || reg.paymentStatus === 'pending'
    ).length;
    const expired = filteredRegistrations.filter((reg) => reg.status === 'expired').length;
    const notFinalized = pending + expired;
    const revenue = filteredRegistrations
      .filter((reg) => reg.paymentStatus === 'paid')
      .reduce((sum, reg) => sum + (reg.amountPaid || 0), 0);
    const fees = filteredRegistrations
      .filter((reg) => reg.paymentStatus === 'paid')
      .reduce((sum, reg) => sum + getRegistrationPlatformFee(reg), 0);
    const feePercentage =
      filteredRegistrations.find((reg) => typeof reg.feePercentage === 'number')?.feePercentage ?? 6.9;
    const conversionPct = total > 0 ? (confirmed / total) * 100 : 0;
    return {
      total,
      confirmed,
      pending,
      expired,
      notFinalized,
      revenue,
      fees,
      netRevenue: Math.max(0, revenue - fees),
      feePercentage,
      conversionPct,
    };
  }, [filteredRegistrations]);

  const capacityMetrics = useMemo(() => {
    const capacity =
      typeof eventData?.capacity === 'number' && eventData.capacity > 0 ? eventData.capacity : null;
    if (!capacity) {
      return {
        capacity: null as number | null,
        remaining: null as number | null,
        occupancyPct: 0,
        paidOccupying: 0,
      };
    }

    // Preferir contagem do backend (mesma regra da página pública / sold-out).
    let paidOccupying =
      typeof eventData?.registeredCount === 'number' ? eventData.registeredCount : null;
    if (paidOccupying == null) {
      // Fallback: soma quantity nas inscrições do evento (não usa filtros da tabela).
      paidOccupying = eventRegistrations
        .filter(
          (reg) =>
            reg.paymentStatus === 'paid' &&
            (reg.status === 'pending' || reg.status === 'confirmed' || reg.status === 'attended')
        )
        .reduce((sum, reg) => {
          const qty = Number(reg.quantity);
          return sum + (Number.isFinite(qty) && qty > 0 ? qty : 1);
        }, 0);
    }

    const remaining =
      typeof eventData?.remainingSpots === 'number'
        ? eventData.remainingSpots
        : Math.max(0, capacity - paidOccupying);
    const occupancyPct = Math.min(100, (paidOccupying / capacity) * 100);
    return { capacity, remaining, occupancyPct, paidOccupying };
  }, [
    eventData?.capacity,
    eventData?.registeredCount,
    eventData?.remainingSpots,
    eventRegistrations,
  ]);

  const ticketOptions = useMemo(
    () => Array.from(new Set(eventRegistrations.map((reg) => reg.ticketName).filter(Boolean))).sort(),
    [eventRegistrations]
  );
  const formDataColumns = useMemo(() => {
    const keys = new Set<string>();
    eventRegistrations.forEach((reg) => {
      if (!reg.formData) return;
      Object.keys(reg.formData).forEach((key) => {
        if (!isBasicFormFieldKey(key)) {
          keys.add(key);
        }
      });
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [eventRegistrations]);

  useEffect(() => {
    setVisibleColumns((prev) => {
      const next = { ...prev };
      formDataColumns.forEach((col) => {
        const key = `formData_${col}`;
        if (!(key in next)) {
          next[key] = false;
        }
      });
      return next;
    });
  }, [formDataColumns]);

  const paginatedRegistrations = useMemo(
    () => filteredRegistrations.slice((registrationsPage - 1) * rowsPerPage, registrationsPage * rowsPerPage),
    [filteredRegistrations, registrationsPage]
  );

  const handleChangeStatus = async (newStatus: 'published' | 'paused' | 'test') => {
    if (!eventData?.slug) return;
    try {
      setStatusUpdating(true);
      const response = await api.put(`/events/${eventData.slug}/status`, { status: newStatus });
      if (response?.data?.success) {
        setEventData((prev) => (prev ? { ...prev, status: newStatus } : prev));
      }
    } catch (err) {
      console.error('Erro ao atualizar status do evento:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleToggleShowRemainingSpots = async (checked: boolean) => {
    if (!eventData?._id) return;
    try {
      setSpotsToggleUpdating(true);
      setError(null);
      await eventService.updateEventFields(eventData._id, { showRemainingSpots: checked });
      setEventData((prev) => (prev ? { ...prev, showRemainingSpots: checked } : prev));
    } catch (err) {
      console.error('Erro ao atualizar vagas restantes:', err);
      setError('Não foi possível atualizar a exibição de vagas restantes.');
    } finally {
      setSpotsToggleUpdating(false);
    }
  };

  const handleExportEventRegistrations = async () => {
    if (!eventData?._id) return;
    try {
      setExporting(true);
      await registrationService.exportEventRegistrations(eventData._id);
    } catch (err) {
      console.error('Erro ao exportar inscrições do evento:', err);
    } finally {
      setExporting(false);
      setExportDialogOpen(false);
    }
  };

  const handleColumnsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnsMenuAnchor(event.currentTarget);
  };

  const handleColumnsMenuClose = () => {
    setColumnsMenuAnchor(null);
  };

  const handleToggleColumn = (column: string) => {
    setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const handleClearFilters = () => {
    setPaymentFilter('all');
    setRegistrationStatusFilter('all');
    setMethodFilter('all');
    setTicketFilter('all');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setRegistrationsPage(1);
  };

  const handleExportXlsx = async () => {
    try {
      setExporting(true);
      if (!eventData?._id) return;
      await registrationService.exportEventRegistrationsXlsx(eventData._id);
      setExportDialogOpen(false);
    } finally {
      setExporting(false);
    }
  };

  const getPaymentStatusChip = (reg: Registration) => (
    <Stack spacing={0.25} alignItems="flex-start">
      <Chip
        label={registrationPaymentLabel(reg)}
        color={registrationPaymentChipColor(reg)}
        size="small"
      />
      {(reg.paymentAttemptCount || 0) > 0 ? (
        <Typography variant="caption" color={reg.failedPaymentAttemptCount ? 'error' : 'text.secondary'}>
          {reg.paymentAttemptCount} tentativa{reg.paymentAttemptCount === 1 ? '' : 's'}
          {reg.failedPaymentAttemptCount ? ` · ${reg.failedPaymentAttemptCount} com erro` : ''}
        </Typography>
      ) : null}
    </Stack>
  );

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !eventData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Evento não encontrado.'}</Alert>
      </Container>
    );
  }

  const locationLabel = [eventData.city, eventData.state].filter(Boolean).join(', ') || eventData.location || 'Local a definir';

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1.25, sm: 2 }, mb: 6, px: { xs: 1.25, sm: 3 } }}>
      <Breadcrumbs sx={{ mb: 0.5, fontSize: '0.75rem', '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
        <MuiLink
          component={Link}
          to="/dashboard/eventos"
          underline="hover"
          color="text.secondary"
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        >
          Eventos
        </MuiLink>
        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
          Painel do evento
        </Typography>
      </Breadcrumbs>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.25, md: 1 }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 1.5, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.35 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              {eventData.title}
            </Typography>
            <Chip
              label={getEventStatusLabel(eventData.status)}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, height: 22 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {locationLabel} • {eventData.startDate ? new Date(eventData.startDate).toLocaleDateString('pt-BR') : 'Data não definida'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            component={Link}
            to={`/dashboard/eventos/${eventData._id}/editar`}
            variant="outlined"
            startIcon={<EditIcon />}
            size="small"
            sx={{ flex: { xs: 1, md: 'none' }, whiteSpace: 'nowrap' }}
          >
            Editar
          </Button>
          {customDomainPublicUrl ? (
            <Button
              component="a"
              href={customDomainPublicUrl}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              startIcon={<VisibilityIcon />}
              size="small"
              sx={{ flex: { xs: 1, md: 'none' }, whiteSpace: 'nowrap' }}
            >
              Ver página
            </Button>
          ) : (
            <Button
              component={eventData.slug ? Link : 'button'}
              to={eventData.slug ? `/${eventData.slug}` : undefined}
              variant="contained"
              startIcon={<VisibilityIcon />}
              size="small"
              disabled={!eventData.slug}
              sx={{ flex: { xs: 1, md: 'none' }, whiteSpace: 'nowrap' }}
            >
              Ver página
            </Button>
          )}
        </Stack>
      </Stack>

      {tab !== workspaceTabs.marketing ? (
        <MetaIntegrationNotice
          variant="banner"
          pixelId={eventData.metaPixel?.pixelId}
          enabled={eventData.metaPixel?.enabled}
          capiTokenConfigured={eventData.metaPixel?.capiTokenConfigured}
          onOpenMarketing={() => setTab(workspaceTabs.marketing)}
        />
      ) : null}

      <Paper
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          mb: 2,
          overflow: 'hidden',
          position: { xs: 'sticky', md: 'static' },
          top: { xs: 56, sm: 64 },
          zIndex: 8,
          bgcolor: 'background.paper',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile
          sx={{
            minHeight: 44,
            '& .MuiTabs-flexContainer': { gap: { xs: 0.25, sm: 0 } },
            '& .MuiTab-root': {
              minHeight: 44,
              minWidth: 'auto',
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              textTransform: 'none',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab label={t('Inscrições')} />
          <Tab label="Financeiro" />
          {showSaqueTab ? <Tab label="Saque" /> : null}
          <Tab label="Configurações" />
          <Tab label="Formulário" />
          <Tab label="Check-in" />
          {showDomainTab ? <Tab label="Domínio" /> : null}
          <Tab label="Atendimento" />
          <Tab
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>Marketing</span>
                {readMetaIntegration(eventData.metaPixel || {}).complete ? null : (
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }}
                    aria-label="Integração da Meta incompleta"
                  />
                )}
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <>
          <Paper sx={{ p: { xs: 1.25, md: 2 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
              sx={{ mb: 1.25 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {t('Visão Financeira de Inscrições')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Receita, taxas e conversão deste evento.
                </Typography>
              </Box>
              {hasActiveFilters ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color="primary"
                    label={`Filtros aplicados (${activeFilterCount})`}
                    sx={{ fontWeight: 600 }}
                  />
                  <Button size="small" variant="text" onClick={handleClearFilters}>
                    Limpar filtros
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              <Grid item xs={12} sm={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.25, md: 1.75 },
                    borderRadius: 3,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid',
                    borderLeftColor: 'primary.main',
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                    <AccountBalanceWalletOutlinedIcon color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                      VOCÊ RECEBE
                    </Typography>
                    <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  </Stack>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.75, fontSize: { xs: '1.35rem', md: '1.6rem' }, color: 'success.dark' }}>
                    {formatCurrency(filteredMetrics.netRevenue)}
                  </Typography>
                  <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" useFlexGap>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Receita bruta
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {formatCurrency(filteredMetrics.revenue)}
                      </Typography>
                    </Box>
                    <Box>
                      <Stack direction="row" spacing={0.4} alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Taxas ({Number(filteredMetrics.feePercentage).toLocaleString('pt-BR')}%)
                        </Typography>
                        <Tooltip title={t('Taxa da plataforma sobre as inscrições pagas deste evento')}>
                          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        </Tooltip>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                        - {formatCurrency(filteredMetrics.fees)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={4} sm={4} md={2}>
                <Paper
                  elevation={0}
                  sx={{ p: { xs: 1.1, md: 1.75 }, borderRadius: 3, height: '100%', border: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      CONFIRMADAS
                    </Typography>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.main',
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <HowToRegOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Stack>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '1.15rem', md: '1.5rem' } }}>
                    {filteredMetrics.confirmed.toLocaleString('pt-BR')}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={4} sm={4} md={2}>
                <Paper
                  elevation={0}
                  sx={{ p: { xs: 1.1, md: 1.75 }, borderRadius: 3, height: '100%', border: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }} spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      TOTAL
                    </Typography>
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${filteredMetrics.conversionPct.toFixed(0)}%`}
                      sx={{ fontWeight: 600, height: 20, fontSize: { xs: '0.65rem', sm: '0.75rem' }, display: { xs: 'none', sm: 'inline-flex' } }}
                    />
                  </Stack>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '1.15rem', md: '1.5rem' } }}>
                    {filteredMetrics.total.toLocaleString('pt-BR')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {filteredMetrics.pending === 1
                      ? t('1 inscrição pendente')
                      : t(`${filteredMetrics.pending.toLocaleString('pt-BR')} inscrições pendentes`)}
                  </Typography>
                  <Typography variant="caption" color="primary.main" sx={{ display: { xs: 'block', sm: 'none' }, fontWeight: 700 }}>
                    {filteredMetrics.conversionPct.toFixed(0)}%
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={4} sm={4} md={3}>
                <Paper
                  elevation={0}
                  sx={{ p: { xs: 1.1, md: 1.75 }, borderRadius: 3, height: '100%', border: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      VAGAS
                    </Typography>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.main',
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ConfirmationNumberOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Stack>
                  {capacityMetrics.capacity != null && capacityMetrics.remaining != null ? (
                    <>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.2,
                          fontSize: { xs: '1.05rem', md: '1.5rem' },
                          color: capacityMetrics.remaining <= 0 ? 'error.main' : 'text.primary',
                        }}
                      >
                        {capacityMetrics.remaining.toLocaleString('pt-BR')}
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75em' }}>
                          {' / '}
                          {capacityMetrics.capacity.toLocaleString('pt-BR')}
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        restantes · {capacityMetrics.paidOccupying.toLocaleString('pt-BR')} ocupadas
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, display: { xs: 'none', sm: 'flex' } }}>
                        <LinearProgress
                          variant="determinate"
                          value={capacityMetrics.occupancyPct}
                          color={capacityMetrics.occupancyPct >= 90 ? 'error' : capacityMetrics.occupancyPct >= 70 ? 'warning' : 'primary'}
                          sx={{ flex: 1, height: 6, borderRadius: 99 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {capacityMetrics.occupancyPct.toFixed(0)}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={capacityMetrics.occupancyPct}
                        color={capacityMetrics.occupancyPct >= 90 ? 'error' : capacityMetrics.occupancyPct >= 70 ? 'warning' : 'primary'}
                        sx={{ mt: 0.75, height: 4, borderRadius: 99, display: { xs: 'block', sm: 'none' } }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.secondary', lineHeight: 1.2 }}>
                        —
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                        Defina a capacidade
                      </Typography>
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: { xs: 1.25, md: 2 }, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {t('Inscrições do evento')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {filteredRegistrations.length === eventRegistrations.length
                    ? t(`Mostrando todas as ${filteredRegistrations.length} inscrições`)
                    : t(`Mostrando ${filteredRegistrations.length} de ${eventRegistrations.length} inscrições`)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  onClick={() => setExportDialogOpen(true)}
                  disabled={exporting || !filteredRegistrations.length}
                  startIcon={<FileDownloadIcon />}
                  size="small"
                  variant="outlined"
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  {exporting ? '...' : 'Exportar'}
                </Button>
                <Button
                  variant={showAdvancedFilters ? 'contained' : 'outlined'}
                  startIcon={<FilterAltIcon />}
                  onClick={() => setShowAdvancedFilters((prev) => !prev)}
                  size="small"
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Filtros
                </Button>
                <Tooltip title="Escolha quais colunas exibir na tabela">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ViewColumnIcon />}
                    onClick={handleColumnsMenuOpen}
                    sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                  >
                    Colunas
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={7} md={5}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('Buscar')}
                  placeholder="Nome, e-mail, telefone ou código"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setRegistrationsPage(1);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={5} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Pagamento</InputLabel>
                  <Select
                    value={paymentFilter}
                    label="Pagamento"
                    onChange={(e) => {
                      setPaymentFilter(e.target.value as any);
                      setRegistrationsPage(1);
                    }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="paid">Pago</MenuItem>
                    <MenuItem value="pending">Aguardando pagamento</MenuItem>
                    <MenuItem value="expired">Expirado</MenuItem>
                    <MenuItem value="refunded">Reembolsado</MenuItem>
                    <MenuItem value="failed">Falhou</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 0, md: 1.2 } }}>
                  {t(`${filteredRegistrations.length} inscrição(ões) encontrada(s)`)}
                </Typography>
              </Grid>
            </Grid>

            <Collapse in={showAdvancedFilters}>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('Status da inscrição')}</InputLabel>
                    <Select
                      value={registrationStatusFilter}
                      label={t('Status da inscrição')}
                      onChange={(e) => {
                        setRegistrationStatusFilter(e.target.value as any);
                        setRegistrationsPage(1);
                      }}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="confirmed">Confirmada</MenuItem>
                      <MenuItem value="pending">Pendente</MenuItem>
                      <MenuItem value="attended">Compareceu</MenuItem>
                      <MenuItem value="cancelled">Cancelada</MenuItem>
                      <MenuItem value="expired">Expirada</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Método</InputLabel>
                    <Select
                      value={methodFilter}
                      label="Método"
                      onChange={(e) => {
                        setMethodFilter(e.target.value);
                        setRegistrationsPage(1);
                      }}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      {PAYMENT_METHOD_FILTER_OPTIONS.map((method) => (
                        <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ingresso</InputLabel>
                    <Select
                      value={ticketFilter}
                      label="Ingresso"
                      onChange={(e) => {
                        setTicketFilter(e.target.value);
                        setRegistrationsPage(1);
                      }}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      {ticketOptions.map((ticket) => (
                        <MenuItem key={ticket} value={ticket}>{ticket}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Início"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setRegistrationsPage(1);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Fim"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setRegistrationsPage(1);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Collapse>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {visibleColumns.codigo && <TableCell>Código</TableCell>}
                      {visibleColumns.participante && <TableCell>Participante</TableCell>}
                      {visibleColumns.email && <TableCell>Email</TableCell>}
                      {visibleColumns.telefone && <TableCell>Telefone</TableCell>}
                      {visibleColumns.ingresso && <TableCell>Ingresso</TableCell>}
                      {visibleColumns.pagamento && <TableCell>Pagamento</TableCell>}
                      {visibleColumns.metodo && <TableCell>Método</TableCell>}
                      {visibleColumns.valor && <TableCell align="right">Valor</TableCell>}
                      {visibleColumns.data && <TableCell>Data</TableCell>}
                      {visibleColumns.checkin && <TableCell>Check-in</TableCell>}
                      {formDataColumns.map((col) =>
                        visibleColumns[`formData_${col}`] ? <TableCell key={col}>{col}</TableCell> : null
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRegistrations.map((reg) => (
                      <TableRow
                        key={reg.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedRegistration(reg);
                          setDetailsOpen(true);
                        }}
                      >
                        {visibleColumns.codigo && <TableCell>{reg.confirmationCode || '-'}</TableCell>}
                        {visibleColumns.participante && (
                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                              <span>{reg.participantName}</span>
                              <GroupOrderChip count={reg.orderAttendeeCount} />
                            </Stack>
                          </TableCell>
                        )}
                        {visibleColumns.email && <TableCell>{reg.participantEmail}</TableCell>}
                        {visibleColumns.telefone && (
                          <TableCell>
                            <RegistrationPhoneCell formData={reg.formData} />
                          </TableCell>
                        )}
                        {visibleColumns.ingresso && <TableCell>{reg.ticketName}</TableCell>}
                        {visibleColumns.pagamento && <TableCell>{getPaymentStatusChip(reg)}</TableCell>}
                        {visibleColumns.metodo && <TableCell>{paymentMethodDisplay(reg.paymentMethod)}</TableCell>}
                        {visibleColumns.valor && (
                          <TableCell align="right">{reg.amountPaid > 0 ? formatCurrency(reg.amountPaid) : 'Gratuito'}</TableCell>
                        )}
                        {visibleColumns.data && <TableCell>{formatDate(reg.createdAt)}</TableCell>}
                        {visibleColumns.checkin && (
                          <TableCell>
                            {reg.checkedIn ? (
                              <Chip size="small" color="success" label={reg.checkedInAt ? formatDate(reg.checkedInAt) : 'Sim'} />
                            ) : (
                              <Chip size="small" variant="outlined" label="Pendente" />
                            )}
                          </TableCell>
                        )}
                        {formDataColumns.map((col) =>
                          visibleColumns[`formData_${col}`] ? <TableCell key={col}>{String(reg.formData?.[col] ?? '-')}</TableCell> : null
                        )}
                      </TableRow>
                    ))}
                    {!filteredRegistrations.length && (
                      <TableRow>
                        <TableCell colSpan={Math.max(1, Object.values(visibleColumns).filter(Boolean).length + formDataColumns.filter((col) => visibleColumns[`formData_${col}`]).length)} align="center">
                          {t('Nenhuma inscrição para este evento.')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Stack spacing={1}>
                {paginatedRegistrations.map((reg) => (
                  <Paper
                    key={reg.id}
                    variant="outlined"
                    sx={{
                      p: 1.35,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      borderColor: 'divider',
                      '&:active': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => {
                      setSelectedRegistration(reg);
                      setDetailsOpen(true);
                    }}
                  >
                    <Stack spacing={0.85}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>
                            {reg.participantName || 'Participante'}
                          </Typography>
                          <GroupOrderChip count={reg.orderAttendeeCount} />
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', display: 'block' }}>
                            {reg.participantEmail || '-'}
                          </Typography>
                        </Box>
                        {getPaymentStatusChip(reg)}
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                          {reg.ticketName || 'Ingresso'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {reg.amountPaid > 0 ? formatCurrency(reg.amountPaid) : 'Gratuito'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box onClick={(e) => e.stopPropagation()}>
                          <RegistrationPhoneCell formData={reg.formData} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(reg.createdAt)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
                {!filteredRegistrations.length && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('Nenhuma inscrição para este evento.')}
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </Box>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={Boolean(columnsMenuAnchor)}
              onClose={handleColumnsMenuClose}
              PaperProps={{ sx: { maxHeight: 500, minWidth: 250 } }}
            >
              <MenuItem disabled sx={{ fontWeight: 600, opacity: 1 }}>
                Colunas padrão
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('codigo')}>
                <Checkbox checked={visibleColumns.codigo} size="small" />
                Código
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('participante')}>
                <Checkbox checked={visibleColumns.participante} size="small" />
                Participante
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('email')}>
                <Checkbox checked={visibleColumns.email} size="small" />
                Email
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('telefone')}>
                <Checkbox checked={visibleColumns.telefone} size="small" />
                Telefone
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('ingresso')}>
                <Checkbox checked={visibleColumns.ingresso} size="small" />
                Ingresso
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('pagamento')}>
                <Checkbox checked={visibleColumns.pagamento} size="small" />
                Pagamento
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('metodo')}>
                <Checkbox checked={visibleColumns.metodo} size="small" />
                Método
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('valor')}>
                <Checkbox checked={visibleColumns.valor} size="small" />
                Valor
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('data')}>
                <Checkbox checked={visibleColumns.data} size="small" />
                Data
              </MenuItem>
              <MenuItem onClick={() => handleToggleColumn('checkin')}>
                <Checkbox checked={visibleColumns.checkin} size="small" />
                Check-in
              </MenuItem>

              {formDataColumns.length > 0 && (
                <>
                  <MenuItem disabled sx={{ fontWeight: 600, opacity: 1 }}>
                    Campos personalizados
                  </MenuItem>
                  {formDataColumns.map((col) => (
                    <MenuItem key={col} onClick={() => handleToggleColumn(`formData_${col}`)}>
                      <Checkbox checked={visibleColumns[`formData_${col}`] || false} size="small" />
                      {col}
                    </MenuItem>
                  ))}
                </>
              )}
            </Menu>
            {filteredRegistrations.length > rowsPerPage && (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
                <Pagination
                  color="primary"
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                  page={registrationsPage}
                  onChange={(_, value) => setRegistrationsPage(value)}
                  count={Math.ceil(filteredRegistrations.length / rowsPerPage)}
                />
              </Box>
            )}
          </Paper>

          <Dialog open={exportDialogOpen} onClose={() => !exporting && setExportDialogOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>Exportar relatório</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('Escolha o formato de exportação das inscrições deste evento.')}
              </Typography>
              <Stack spacing={1.25}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportEventRegistrations}
                  disabled={exporting}
                >
                  Exportar em CSV
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportXlsx}
                  disabled={exporting}
                >
                  Exportar em XLSX
                </Button>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>
                Cancelar
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Resumo financeiro do evento</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              O valor em destaque é o que a paróquia recebe, depois da taxa da plataforma. A receita bruta é o total pago nas inscrições.
            </Typography>

            <Grid container spacing={1.5} alignItems="stretch">
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main', height: '100%', minHeight: 132, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4, minHeight: 22, display: 'flex', alignItems: 'center' }}>
                    Você recebe
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="success.dark" sx={{ lineHeight: 1.15, mt: 0.25 }}>
                    {formatCurrency(financeMetrics.netRevenue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Líquido das inscrições pagas e confirmadas
                  </Typography>
                  <Stack spacing={0.25} sx={{ mt: 'auto', pt: 1.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      Receita bruta: {formatCurrency(financeMetrics.paidConfirmedRevenue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Taxa da plataforma ({Number(financeMetrics.feePercentage).toLocaleString('pt-BR')}%): − {formatCurrency(financeMetrics.platformFees)}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Paper sx={{ p: 1.75, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', height: '100%', minHeight: 132, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.3, minHeight: 28, display: 'flex', alignItems: 'center' }}>Pagas confirmadas</Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1, minHeight: 50, display: 'flex', alignItems: 'center' }}>{financeMetrics.paidConfirmed.toLocaleString('pt-BR')}</Typography>
                  <Box sx={{ minHeight: 18 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Paper sx={{ p: 1.75, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', height: '100%', minHeight: 132, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.3, minHeight: 28, display: 'flex', alignItems: 'center' }}>Confirmadas gratuitas</Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1, minHeight: 50, display: 'flex', alignItems: 'center' }}>{financeMetrics.freeConfirmed.toLocaleString('pt-BR')}</Typography>
                  <Box sx={{ minHeight: 18 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Paper sx={{ p: 1.75, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', height: '100%', minHeight: 132, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.3, minHeight: 28, display: 'flex', alignItems: 'center' }}>Pendentes</Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1, minHeight: 50, display: 'flex', alignItems: 'center' }}>{financeMetrics.pending.toLocaleString('pt-BR')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18, display: 'flex', alignItems: 'center' }}>{t('Inscrição iniciada')}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Paper sx={{ p: 1.75, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', height: '100%', minHeight: 132, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.3, minHeight: 28, display: 'flex', alignItems: 'center' }}>Expiradas</Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1, minHeight: 50, display: 'flex', alignItems: 'center' }}>{financeMetrics.expired.toLocaleString('pt-BR')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18, display: 'flex', alignItems: 'center' }}>Prazo excedido</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={700}>Desempenho por ingresso/lote</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Cada linha mostra volume e valor por tipo de ingresso (incluindo gratuitos).
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ingresso / Lote</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pagas confirmadas</TableCell>
                    <TableCell align="right">Gratuitas confirmadas</TableCell>
                    <TableCell align="right">Pendentes</TableCell>
                    <TableCell align="right">Expiradas</TableCell>
                    <TableCell align="right">Receita bruta</TableCell>
                    <TableCell align="right">Você recebe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financeMetrics.ticketBreakdown.map((row) => (
                    <TableRow key={row.ticket}>
                      <TableCell>{row.ticket}</TableCell>
                      <TableCell align="right">{row.total.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{row.paidConfirmed.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{row.freeConfirmed.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{row.pending.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{row.expired.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                      <TableCell align="right">{formatCurrency(Math.max(0, row.revenue - row.fees))}</TableCell>
                    </TableRow>
                  ))}
                  {!financeMetrics.ticketBreakdown.length && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">Sem dados de ingressos/lotes para este evento.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 0, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Grid container>
              <Grid item xs={12} md={6} sx={{ p: 2.25 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Saúde operacional</Typography>
                <Box sx={{ display: 'grid', rowGap: 0.9 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Taxa de confirmação</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {metrics.total > 0 ? ((financeMetrics.paidConfirmed / metrics.total) * 100).toFixed(1) : '0.0'}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Valor em pendência</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(financeMetrics.pendingValue)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Receita bruta confirmada</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(financeMetrics.paidConfirmedRevenue)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Taxa da plataforma ({Number(financeMetrics.feePercentage).toLocaleString('pt-BR')}%)
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      − {formatCurrency(financeMetrics.platformFees)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Você recebe</Typography>
                    <Typography variant="body2" fontWeight={800} color="success.dark" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(financeMetrics.netRevenue)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  p: 2.25,
                  borderLeft: { xs: 'none', md: '1px solid' },
                  borderTop: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('Composição de inscrições')}</Typography>
                <Box sx={{ display: 'grid', rowGap: 0.9 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('Total de inscrições')}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {metrics.total.toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Pagas confirmadas</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {financeMetrics.paidConfirmed.toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Gratuitas confirmadas</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {financeMetrics.freeConfirmed.toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Pendentes + expiradas</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {(financeMetrics.pending + financeMetrics.expired).toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      )}

      {showSaqueTab && tab === 2 && eventId && (
        <Box sx={{ '& > .MuiContainer-root': { px: 0, py: 0, maxWidth: '100%' } }}>
          <SaquePage eventId={eventId} eventTitle={eventData?.title} />
        </Box>
      )}

      {tab === workspaceTabs.config && (
        <Paper sx={{ p: 2.5, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Configurações rápidas</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <Button component={Link} to={`/dashboard/eventos/${eventData._id}/editar`} variant="contained" startIcon={<EditIcon />}>
              Editar evento
            </Button>
            <Button component={Link} to="/dashboard/eventos" variant="outlined">
              Voltar para lista de eventos
            </Button>
          </Stack>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Status do evento</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" disabled={statusUpdating} onClick={() => handleChangeStatus('published')}>
              Publicar
            </Button>
            <Button variant="outlined" color="warning" disabled={statusUpdating} onClick={() => handleChangeStatus('paused')}>
              Pausar
            </Button>
            <Button variant="outlined" color="secondary" disabled={statusUpdating} onClick={() => handleChangeStatus('test')}>
              Modo teste
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>Vagas na página pública</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={eventData.showRemainingSpots === true}
                disabled={spotsToggleUpdating || !(typeof eventData.capacity === 'number' && eventData.capacity > 0)}
                onChange={(e) => handleToggleShowRemainingSpots(e.target.checked)}
              />
            }
            label={t('Mostrar vagas restantes para inscrição')}
          />
          <FormHelperText sx={{ mt: 0, ml: 0 }}>
            {typeof eventData.capacity === 'number' && eventData.capacity > 0
              ? `Calculado pela capacidade do evento (${eventData.capacity.toLocaleString('pt-BR')} vagas). Defina a capacidade em Editar evento.`
              : 'Defina uma capacidade em Editar evento para ativar esta opção.'}
          </FormHelperText>
        </Paper>
      )}

      {tab === workspaceTabs.form && eventId && <EventRegistrationQuickEditPanel eventId={eventId} />}

      {tab === workspaceTabs.checkin && eventId && eventData && (
        <EventWorkspaceCheckInTab eventId={eventId} eventTitle={eventData.title} />
      )}

      {showDomainTab && tab === workspaceTabs.domain && eventId && eventData && (
        <EventWorkspaceCustomDomainTab eventId={eventId} eventTitle={eventData.title} />
      )}

      {tab === workspaceTabs.atendimento && eventId && (
        <EventWorkspaceAtendimentoTab eventId={eventId} eventTitle={eventData?.title} />
      )}

      {tab === workspaceTabs.marketing && eventId && eventData && (
        <EventWorkspaceMarketingTab
          eventId={eventId}
          eventTitle={eventData.title}
          onIntegrationChange={(status) =>
            setEventData((prev) => (prev ? { ...prev, metaPixel: status } : prev))
          }
        />
      )}

      <RegistrationDetailsModal
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedRegistration(null);
        }}
        registration={selectedRegistration}
        onChanged={() => {
          if (!eventId) return;
          registrationService.getByEvent(eventId).then((regs) => {
            setEventRegistrations(regs || []);
            const updated = (regs || []).find((r) => r.id === selectedRegistration?.id);
            if (updated) setSelectedRegistration(updated);
          });
        }}
      />
    </Container>
  );
};

export default EventWorkspacePage;
